/**
 * ChatWithAI Component
 *
 * This component provides an interactive chat interface with an AI assistant (model set by CHAT_MODEL env).
 * It's displayed only for players with marker=0 and secondary_marker="ai" during Round 0.
 *
 * Features:
 * - Real-time chat with the OpenAI model configured via CHAT_MODEL (temperature enforced server-side)
 * - Message persistence (saved to player.aiMessages)
 * - Typing indicators and error handling
 * - Clean bubble interface with emerald/teal color scheme
 * - Auto-scrolling message area
 *
 * Data Flow:
 * 1. User types message → added to local state and player scope
 * 2. Message sent to http://localhost:5001/chat (via chat-api.js)
 * 3. OpenAI response returned and displayed
 * 4. All messages saved to player.set("aiMessages")
 *
 * Styling:
 * - User messages: Emerald gradient, right-aligned
 * - AI messages: White with border, left-aligned
 * - Header: Emerald to teal gradient
 */

import React, { useState, useEffect } from "react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import { LatexMessage } from "./chat/LatexMessage";

export function ChatWithAI() {
  const player = usePlayer();
  const game = useGame();

  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [error, setError] = useState(null);

  // Load AI configuration from game config
  const config = game.get("config");

  // Get condition: "ai" (free-form) or "ai-fixed" (structured pedagogical)
  const condition = game.get("condition") || player.get("secondary_marker") || "ai";

  // PDF text content for in-context learning
  const pdfContent = config?.stage1?.pdfTextContent || "";

  // Wikipedia reference URL for additional context
  const wikipediaUrl = config?.stage1?.wikipediaUrl || "";

  // Fixed pedagogical prompt (used for ai-fixed condition)
  const aiFixedPrompt = config?.stage1?.aiFixedPrompt || "";

  // Topic metadata for fallback context when pdfTextContent is empty
  const topicName = config?.name || "the topic";
  const topicDescription = config?.topicDescription || "";
  const instructionExplainer = config?.instructionExplainer || "";
  const formulas = config?.formulas || "";
  const quizQuestions = config?.quiz?.questions || [];

  // System prompt construction based on condition:
  // - "ai": Minimal prompt + reference material (PDF + Wikipedia)
  // - "ai-fixed": Structured pedagogical prompt + reference material
  // Both conditions always include PDF content and Wikipedia URL for context.
  const latexInstructions = "When writing mathematical formulas, use LaTeX with dollar-sign delimiters: $...$ for inline math and $$...$$ ONLY for a standalone displayed equation on its own line. Put each complete formula — including its numbers, units, operators, and exponents — inside a SINGLE delimited span. Never split one expression across multiple spans, and never wrap an individual number, unit, or operator (e.g. a lone $\\cdot$ or $^{-1}$) in its own delimiters. Keep units inside the math span using \\text{} and \\cdot — e.g. write the ideal-gas constant as one span, $R = 0.0821\\ \\text{L}\\cdot\\text{atm}\\cdot\\text{mol}^{-1}\\cdot\\text{K}^{-1}$, not as separate pieces. Do not use $$...$$ for a value, unit, or term that sits inside a sentence.";

  // Build reference material section with PDF content, Wikipedia link, or topic metadata fallback
  let referenceMaterial = "";
  if (pdfContent) {
    // Primary: Use extracted PDF text content
    referenceMaterial = pdfContent;
  } else {
    // Fallback: Build context from topic metadata
    referenceMaterial = `TOPIC: ${topicName}\n\n`;

    if (topicDescription) {
      referenceMaterial += `DESCRIPTION:\n${topicDescription}\n\n`;
    }

    if (instructionExplainer) {
      referenceMaterial += `KEY LEARNING OBJECTIVES:\n${instructionExplainer}\n\n`;
    }

    if (formulas && formulas !== "NA" && formulas !== "null") {
      referenceMaterial += `KEY FORMULAS:\n${formulas}\n\n`;
    }

    // Include quiz question topics (not answers) to guide AI on what to cover
    if (quizQuestions.length > 0) {
      referenceMaterial += `TOPICS TO COVER (based on quiz questions):\n`;
      quizQuestions.slice(0, 6).forEach((q, idx) => {
        // Extract just the question text, not the answer
        const questionText = q.text || q.question || "";
        if (questionText) {
          referenceMaterial += `${idx + 1}. ${questionText}\n`;
        }
      });
      referenceMaterial += "\n";
    }

    referenceMaterial += "Use your knowledge to accurately explain these concepts. Be educational and helpful.";
  }

  if (wikipediaUrl) {
    referenceMaterial += `\n\nFor additional information, refer to: ${wikipediaUrl}`;
  }

  let systemPrompt;
  if (condition === "ai-fixed" && aiFixedPrompt) {
    // AI-FIXED: Structured pedagogical prompt + reference material
    systemPrompt = `${aiFixedPrompt}\n\n${latexInstructions}\n\nREFERENCE MATERIAL:\n${referenceMaterial}`;
  } else {
    // AI (free-form): Helpful tutor + reference material
    systemPrompt = `You are a helpful educational tutor. Help the user learn about ${topicName}. Answer their questions clearly and provide examples when helpful.\n\n${latexInstructions}\n\nREFERENCE MATERIAL:\n${referenceMaterial}`;
  }

  const aiTemperature = config?.stage1?.temperature || 0.7;

  // Load messages from player scope on mount
  useEffect(() => {
    const savedMessages = player.get("aiMessages") || [];
    setMessages(savedMessages);
  }, []);

  // Function to call the OpenAI chat API
  const getChatResponse = async (conversationMessages) => {
    try {
      // Convert to OpenAI format with system prompt
      const openAIMessages = [
        { role: "system", content: systemPrompt },
        ...conversationMessages.map(msg => ({
          role: msg.role,
          content: msg.content
        }))
      ];

      // Use localhost for local development, HTTPS proxy for production
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const chatApiUrl = isLocalhost
        ? "http://localhost:5001/chat"
        : `${window.location.origin}/api/chat`;  // Uses HTTPS via Caddy proxy to port 5001

      const response = await fetch(chatApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messages: openAIMessages,
          temperature: aiTemperature,
        }),
      });

      if (!response.ok) {
        throw new Error(`Chat API returned status ${response.status}`);
      }

      const { chatResponse } = await response.json();
      return chatResponse;
    } catch (err) {
      console.error("Error calling chat API:", err);
      throw err;
    }
  };

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage = {
      role: "user",
      content: inputText.trim(),
      timestamp: Date.now(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputText("");
    setIsAiTyping(true);
    setError(null);

    // Persist the user message immediately — if the API call fails or the
    // page reloads mid-request, the message must not vanish from the
    // transcript (aiMessages is a core behavioral measure)
    player.set("aiMessages", updatedMessages);

    try {
      // Get AI response
      const aiResponse = await getChatResponse(updatedMessages);

      const aiMessage = {
        role: "assistant",
        content: aiResponse,
        timestamp: Date.now(),
      };

      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);

      // Save messages to player scope
      player.set("aiMessages", finalMessages);
    } catch (err) {
      setError("Failed to get AI response. Please try again.");
      console.error(err);
    } finally {
      setIsAiTyping(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="chat-with-ai flex flex-col h-full bg-white rounded-lg shadow-lg border border-gray-200">
      {/* Simple Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-3 rounded-t-lg">
        <h3 className="text-lg font-semibold">AI Assistant</h3>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <p className="text-sm">Ask a question to get started</p>
          </div>
        )}

        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div className={`max-w-[85%] ${msg.role === "user" ? "order-2" : "order-1"}`}>
              <div className={`text-xs mb-1 px-3 ${msg.role === "user" ? "text-right text-gray-600" : "text-left text-gray-600"}`}>
                {msg.role === "user" ? "You" : "AI Assistant"}
              </div>
              <div
                className={`rounded-2xl px-5 py-3 ${
                  msg.role === "user"
                    ? "bg-emerald-500 text-white rounded-br-sm"
                    : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm"
                }`}
              >
                <div className="break-words text-base leading-relaxed">
                  <LatexMessage text={msg.content} coalesceMath />
                </div>
              </div>
            </div>
          </div>
        ))}

        {isAiTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }}></div>
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Input area - expandable textarea like social media */}
      <div className="border-t border-gray-200 p-4 bg-white rounded-b-lg">
        <div className="flex space-x-2 items-end">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type your message..."
            disabled={isAiTyping}
            rows={1}
            className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none overflow-hidden"
            style={{
              minHeight: '48px',
              maxHeight: '150px',
              height: 'auto',
            }}
            onInput={(e) => {
              // Auto-expand textarea as user types
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
            }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isAiTyping}
            className="bg-emerald-500 text-white px-6 py-3 rounded-xl hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium text-base flex-shrink-0"
          >
            Send
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">Press Enter to send, Shift+Enter for new line</p>
      </div>
    </div>
  );
}